import { getSessionId, identify, Identify, init, setDeviceId, setUserId, track, reset } from "@amplitude/analytics-browser";
import { loadAmplitudeTrackingRatio } from "@/common/utils/storage";
import { ValidPropertyType } from "@amplitude/analytics-types/lib/esm/event";
import { IUser } from "@/common/services/types";
import { isTrackingEnabled } from "@/common/utils/tracking";
import { isCookieDeclined } from "@/common/utils/cookie";

export interface IProperties {
  [key: string]: {
    value: ValidPropertyType;
    type: "set" | "setOnce";
  };
}

const API_KEY = import.meta.env.REACT_APP_AMPLITUDE_KEY;

/**
 * For naming conventions follow these rules:
 * https://help.amplitude.com/hc/en-us/articles/115000465251-Data-planning-playbook
 */
class AmplitudeAnalytics {
  private trackingEnabled: boolean | null = null;
  private initPromise: Promise<boolean> | null = null;

  private userPropertiesTimeout: NodeJS.Timeout | undefined;
  private userPropertiesBatch: IProperties = {};

  async isTrackingEnabled() {
    if (this.trackingEnabled === null) {
      const defaultTrackingRatio = 0.06;
      const trackingRatio = loadAmplitudeTrackingRatio() ?? defaultTrackingRatio;

      if (await isCookieDeclined(["analytics"])) {
        this.trackingEnabled = false;
        return this.trackingEnabled;
      }

      this.trackingEnabled = await isTrackingEnabled(trackingRatio);

      if (this.trackingEnabled) {
        await this.init();
      }
    }

    return this.trackingEnabled;
  }

  async init() {
    if (!this.initPromise) {
      this.initPromise = new Promise(resolve => {
        setTimeout(async () => {
          try {
            const { promise } = init(API_KEY, { defaultTracking: false });
            await promise;
            resolve(true);
          } catch (e) {
            console.error(`Amplitude SDK initialization error: ${e}`);
            resolve(false);
          }
        }, 0);
      });
    }

    return await this.initPromise;
  }

  async identifyUser(user: IUser) {
    await this.init();

    setUserId(user.username);

    return this.updateUserProperties({
      "Verified": {
        value: user.verified,
        type: "set"
      },
    });
  }

  async resetIdentity() {
    await this.init();

    reset();
  }

  async setDeviceId(deviceId: string) {
    await this.init();

    setDeviceId(deviceId);
  }

  async getSessionId() {
    await this.init();

    return getSessionId();
  }

  async updateUserProperties(properties: IProperties) {
    if (!await this.isTrackingEnabled()) {
      return;
    }

    const batchDelay = 300;
    const identity = new Identify();

    this.userPropertiesBatch = { ...this.userPropertiesBatch, ...properties };

    clearTimeout(this.userPropertiesTimeout);
    this.userPropertiesTimeout = setTimeout(async () => {
      Object.entries(this.userPropertiesBatch).forEach(([key, property]) => {
        identity[property.type](key, property.value);
      });

      this.userPropertiesBatch = {};
      await identify(identity).promise;
    }, batchDelay);
  }

  sendForcedFullscreenEntered(tag?: string) {
    return this.sendTrack("Forced Fullscreen Entered", {
      "Tag": tag,
    });
  }

  sendForcedFullscreenExitClicked(tag?: string) {
    return this.sendTrack("Forced Fullscreen Exit Clicked", {
      "Tag": tag,
    });
  }

  sendUploadStarted(username: string) {
    return this.sendTrack("Upload: Started", {
      "Username": username,
    });
  }

  sendUploadTagSelectionStarted(username: string) {
    return this.sendTrack("Upload: Tag Selection Started", {
      "Username": username,
    });
  }

  sendUploadTagSelectionEnded(username: string, tagsNumber: number) {
    return this.sendTrack("Upload: Tag Selection Ended", {
      "Tags Selected": tagsNumber,
      "Username": username,
    });
  }

  sendUploadEnded(username: string) {
    return this.sendTrack("Upload: Ended", {
      "Username": username,
    });
  }

  sendUploadFinished(username: string, sourceURL?: "local" | "url") {
    return this.sendTrack("Upload: Finished", {
      "Source Url": sourceURL,
      "Username": username,
    });
  }

  sendUploadSelected(username: string, type: "image" | "video" | "bulkImages" | "url") {
    return this.sendTrack("Upload: Selected", {
      "Type": type,
      "Username": username,
    });
  }

  sendUploadSourceDropped(username: string, type: "video" | "image" | "images") {
    return this.sendTrack("Upload: Source Dropped", {
      "Type": type,
      "Username": username,
    });
  }

  sendUploadSourceConfirmed(username: string, type: "video" | "image" | "multiple") {
    return this.sendTrack("Upload: Source Confirmed", {
      "Type": type,
      "Username": username,
    });
  }

  sendUploadAudienceDefined(username: string) {
    return this.sendTrack("Upload: Audience Defined", {
      "Username": username,
    });
  }

  sendUploadVideoTrimmed(username: string) {
    return this.sendTrack("Upload: Video Trimmed", {
      "Username": username,
    });
  }

  sendManageItemInteraction(id: string, username: string, interaction: string) {
    return this.sendTrack("Manage Item Interaction", {
      "Id": id,
      "Username": username,
      "Interaction": interaction,
    });
  }

  sendManagePageInteraction(username: string, interaction: string) {
    return this.sendTrack("Manage Page Interaction", {
      "Username": username,
      "Interaction": interaction,
    });
  }

  sendManagePageVisited(username: string) {
    return this.sendTrack("Manage Page Visited", {
      "Username": username,
    });
  }

  sendWatchPageVisited(isLanding: boolean, source: string, tag?: string) {
    return this.sendTrack("Watch Page Visited", {
      "Is Landing": isLanding,
      "Source": source,
      "Tag": tag,
    });
  }

  sendHomePageVisited(isLanding: boolean) {
    return this.sendTrack("Home Page Visited", {
      "Is Landing": isLanding
    });
  }

  sendPWAPageVisited() {
    return this.sendTrack("App Page Visited", {
      "URL": window.location.pathname
    });
  }

  sendPWASessionStarted() {
    return this.sendTrack("PWA Session Started");
  }

  sendGifViewed(id: string, isFullscreen: boolean, source: string, tag?: string) {
    return this.sendTrack("Gif Viewed", {
      "URL": window.location.pathname,
      "Id": id,
      "Fullscreen": isFullscreen,
      "Source": source,
      "Tag": tag,
    });
  }

  sendGifViewedMeaningful(id: string, isFullscreen: boolean, source: string, percent: number, tag?: string) {
    return this.sendTrack("Gif Viewed Meaningful", {
      "URL": window.location.pathname,
      "Id": id,
      "Fullscreen": isFullscreen,
      "Source": source,
      "Tag": tag,
      "Percent Viewed": percent,
    });
  }

  sendGifLiked(id: string, isFullscreen: boolean, source: string, isMainGIF?: boolean, tag?: string) {
    return this.sendTrack("Gif Liked", {
      "URL": window.location.pathname,
      "Id": id,
      "Fullscreen": isFullscreen,
      "Source": source,
      "Tag": tag,
      ...(isMainGIF !== undefined ? { "Main GIF": isMainGIF } : {}),
    });
  }

  sendGifUnliked(id: string, isFullscreen: boolean, source: string, isMainGIF?: boolean, tag?: string) {
    return this.sendTrack("Gif Unliked", {
      "URL": window.location.pathname,
      "Id": id,
      "Fullscreen": isFullscreen,
      "Source": source,
      "Tag": tag,
      ...(isMainGIF !== undefined ? { "Main GIF": isMainGIF } : {})
    });
  }

  sendGifAddedToCollection(id: string, isFullscreen: boolean, source: string, tag?: string) {
    return this.sendTrack("Gif Added To Collection", {
      "URL": window.location.pathname,
      "Id": id,
      "Fullscreen": isFullscreen,
      "Source": source,
      "Tag": tag,
    });
  }

  sendAnonymouslyClickedFollowCreator(username: string, source: string, tag?: string) {
    return this.sendTrack("Follow Attempt", {
      "URL": window.location.pathname,
      "Source": source,
      "Username": username,
      "Tag": tag,
    });
  }

  sendFollowedCreator(username: string, source: string, tag?: string) {
    return this.sendTrack("Follow", {
      "URL": window.location.pathname,
      "Source": source,
      "Username": username,
      "Tag": tag,
    });
  }

  sendUnfollowedCreator(username: string, source: string, tag?: string) {
    return this.sendTrack("Unfollow", {
      "URL": window.location.pathname,
      "Source": source,
      "Username": username,
      "Tag": tag,
    });
  }

  sendFullscreenEntered(tag?: string) {
    return this.sendTrack("Fullscreen Entered", {
      "URL": window.location.pathname,
      "Tag": tag,
    });
  }

  sendFullscreenExited(tag?: string) {
    return this.sendTrack("Fullscreen Exited", {
      "URL": window.location.pathname,
      "Tag": tag,
    });
  }

  sendNavButtonClicked(section: string) {
    return this.sendTrack("Nav Button Clicked", {
      "URL": window.location.pathname,
      "Page Title": document.title,
      "Section": section
    });
  }

  sendSearchBarClicked() {
    return this.sendTrack("Search Bar Clicked", {
      "URL": window.location.pathname,
      "Page Title": document.title
    });
  }

  sendSearchQuerySubmitted(query: string, queryType: string, queryCount: number) {
    return this.sendTrack("Search Query Submitted", {
      "URL": window.location.pathname,
      "Page Title": document.title,
      "Query": query,
      "Query Type": queryType,
      "Query Count": queryCount,
    });
  }

  sendLoginCompleted() {
    return this.sendTrack("Login Completed", {
      "URL": window.location.pathname,
      "Page Title": document.title
    });
  }

  sendSearchResultClicked(section: string, title: string, position: number, initiator: string) {
    return this.sendTrack("Search Result Clicked", {
      "Item Type": "Item",
      "Item Section": section,
      "Item Title": title,
      "Item Position": position,
      "Search Initiator": initiator
    });
  }

  sendSeeAllButtonClicked(section: string, tag?: string, initiator?: string) {
    return this.sendTrack("See All Clicked", {
      "URL": window.location.pathname,
      "Section": section,
      "Tag": tag,
      "Search Initiator": initiator
    });
  }

  sendQuickRecsShown(tag?: string) {
    return this.sendTrack("Context Rec: Shown", {
      "Tag": tag,
    });
  }

  sendQuickRecsClosed(type: string, tag?: string) {
    return this.sendTrack("Context Rec: Closed", {
      "Type": type,
      "Tag": tag,
    });
  }

  sendQuickRecsItemClicked(position: number, tag?: string) {
    return this.sendTrack("Context Rec: Item Click", {
      "Position": position,
      "Tag": tag,
    });
  }

  sendNicheGifVoteUpClicked(niche: string, gif: string, tag?: string) {
    return this.sendTrack("Up Vote Niche Gif", {
      "Niche": niche,
      "Gif": gif,
      "Tag": tag,
    });
  }

  sendNicheGifVoteDownClicked(niche: string, gif: string, tag?: string) {
    return this.sendTrack("Down Vote Niche Gif", {
      "Niche": niche,
      "Gif": gif,
      "Tag": tag,
    });
  }

  sendJoinNicheClicked(nicheId: string, source: string, tag?: string) {
    return this.sendTrack("Join Niche", {
      "Niche Id": nicheId,
      "Tag": tag,
      "Source": source,
    });
  }

  sendJoinNicheClickedAnonymously(nicheId: string, source: string, tag?: string) {
    return this.sendTrack("Join Niche Attempt", {
      "Niche Id": nicheId,
      "Tag": tag,
      "Source": source,
    });
  }

  sendLeaveNicheClicked(nicheId: string, tag?: string) {
    return this.sendTrack("Leave Niche", {
      "Niche Id": nicheId,
      "Tag": tag,
    });
  }

  sendNichePreviewClicked(nicheId: string, source: string, initiator: string) {
    return this.sendTrack("Niche Preview Clicked", {
      "Niche Id": nicheId,
      "Source": source,
      "Initiator": initiator,
    });
  }

  sendProfileClicked(username: string, source: string, tag?: string) {
    return this.sendTrack("Profile Clicked", {
      "Profile Name": username,
      "Source": source,
      "Tag": tag,
    });
  }

  sendTagClicked(tag: string, source: string, section: string) {
    return this.sendTrack("Tag Clicked", {
      "Source": source,
      "Tag": tag,
      "Section": section,
    });
  }

  sendPageClosed(
    page: "watch" | "gifs" | "niche",
    type: string,
    gifsViewed: number,
    gifsViewedMeaningful: number,
    timeSpent: number,
    contentLength: number,
    tag?: string,
  ) {
    const capitalizedPage = page.charAt(0).toUpperCase() + page.slice(1);

    return this.sendTrack(`${capitalizedPage} Page Closed`, {
      "Type": type,
      "GIFs viewed": gifsViewed,
      "GIFs viewed meaningful": gifsViewedMeaningful,
      "Time spent": timeSpent,
      "Content length": contentLength,
      "Tag": tag,
    });
  }

  private async sendTrack(eventName: string, eventProperties?: Record<string, string | boolean | number | undefined>) {
    if (!await this.isTrackingEnabled()) {
      return;
    }

    return track(eventName, eventProperties);
  }
}

export default new AmplitudeAnalytics();
